import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from './AuthProvider';
import { UserRole, RouteProtectionOptions, AllowedRoles } from '../types';

export interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: AllowedRoles;
  redirectTo?: string;
  fallbackMessage?: string;
  requireEmailVerification?: boolean;
}

/**
 * Protected route component that checks authentication and role-based access
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles = 'authenticated',
  redirectTo = '/login',
  fallbackMessage = 'Access Denied',
  requireEmailVerification = false,
}) => {
  const auth = useAuthContext();
  const location = useLocation();

  // Show loading state
  if (auth.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user is authenticated
  if (!auth.isAuthenticated || !auth.user) {
    // Redirect to login with return URL
    return <Navigate to={{ pathname: redirectTo  }} state= {{ from: location.pathname }} />;
  }

  // Check email verification if required
  if (requireEmailVerification && !auth.user.isEmailVerified) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 className="text-lg font-semibold text-yellow-800">Email Verification Required</h3>
          <p className="mt-2 text-yellow-700">
            Please verify your email address before accessing this page.
          </p>
          <button
            onClick={() => auth.signOut()}
            className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Check role-based access
  const hasAccess = checkRoleAccess(auth.user.role, allowedRoles);
  
  if (!hasAccess) {
    // Show access denied for authenticated users without proper role
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-6 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-lg font-semibold text-red-800">{fallbackMessage}</h3>
          <p className="mt-2 text-red-700">
            You don't have permission to access this page. Required role(s):{' '}
            {typeof allowedRoles === 'string' ? allowedRoles : allowedRoles.join(', ')}
          </p>
          <div className="mt-4 space-x-4">
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Go Back
            </button>
            <button
              onClick={() => auth.signOut()}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // All checks passed, render children
  return <>{children}</>;
};

/**
 * Check if user has access based on allowed roles
 */
function checkRoleAccess(userRole: UserRole, allowedRoles: AllowedRoles): boolean {
  if (allowedRoles === 'all') {
    return true;
  }

  if (allowedRoles === 'authenticated') {
    return true; // Already authenticated check passed
  }

  if (Array.isArray(allowedRoles)) {
    return allowedRoles.includes(userRole);
  }

  return false;
}

/**
 * Higher-order component for protecting routes
 */
export const withProtectedRoute = <P extends object>(
  Component: React.ComponentType<P>,
  options: RouteProtectionOptions = { allowedRoles: 'authenticated' }
): React.FC<P> => {
  const WithProtectedRoute: React.FC<P> = (props) => (
    <ProtectedRoute
      allowedRoles={options.allowedRoles}
      redirectTo={options.redirectTo}
      fallbackMessage={options.fallbackMessage}
    >
      <Component {...props} />
    </ProtectedRoute>
  );

  WithProtectedRoute.displayName = `WithProtectedRoute(${Component.displayName || Component.name})`;
  return WithProtectedRoute;
};